import axios from "axios";
import { getSubscriptionAccessToken } from "helpers/AuthHelpers";
import { getRegionBasedBackendUrl } from "helpers/utils";
import { jwtDecode } from "jwt-decode";

import * as authHelper from "../helpers/AuthHelpers";
import { SAVING_PAUSED_EVENT, maintenanceClient } from "components/maintenance/useMaintenance";

const { REACT_APP_CUSTOM_HEADER_KEY, REACT_APP_CUSTOM_HEADER_VALUE } =
  process.env;
const bufferExpirySeconds = 600;
interface JwtPayload {
  exp: number;
  iat?: number;
  [key: string]: any;
}

let globalTokenRefreshHandler: ((url: string) => Promise<string>) | null = null;

// Function to set the token refresh handler from AxiosInterceptor
export const setTokenRefreshHandler = (
  handler: (url: string) => Promise<string>
) => {
  globalTokenRefreshHandler = handler;
};

// Helper function to check if token is expiring soon
const isTokenExpiringSoon = (
  token: string,
  bufferSeconds: number = bufferExpirySeconds
): boolean => {
  try {
    const decoded: JwtPayload = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const expiryTime = decoded.exp;

    return expiryTime - currentTime <= bufferSeconds;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
};

// Helper function to get token based on URL
const getTokenForUrl = (url: string): string => {
  return getSubscriptionAccessToken();
};

// Helper function to refresh token if needed
const refreshTokenIfExpiring = async (
  token: string,
  url: string
): Promise<string> => {
  if (
    !isTokenExpiringSoon(token, bufferExpirySeconds) ||
    !globalTokenRefreshHandler
  ) {
    return token;
  }

  try {
    const newToken = await globalTokenRefreshHandler(url);
    if (newToken) {
      authHelper.setSubscriptionAccessToken(newToken);
      return newToken;
    }
  } catch (refreshError) {
    console.error("Proactive token refresh failed:", refreshError);
  }

  return token;
};

// Helper function to set custom headers
const setCustomHeaders = (config: any): void => {
  config.headers["Content-Type"] = "application/json";

  if (REACT_APP_CUSTOM_HEADER_KEY && REACT_APP_CUSTOM_HEADER_VALUE) {
    config.headers[REACT_APP_CUSTOM_HEADER_KEY] = REACT_APP_CUSTOM_HEADER_VALUE;
  }
};

const apiInterceptor = axios.create({
  baseURL: getRegionBasedBackendUrl() ?? "",
});

export function updateHttpClientBaseUrl() {
  apiInterceptor.defaults.baseURL = getRegionBasedBackendUrl();
}

apiInterceptor.interceptors.request.use(
  async (config: any) => {
    const { url } = config;
    let token = getTokenForUrl(url);

    if (token) {
      token = await refreshTokenIfExpiring(token, url);
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    setCustomHeaders(config);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiInterceptor.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    signalMaintenance(error);
    return Promise.reject(error);
  }
);

/**
 * A request that never reached a working server (network error or 5xx) asks the maintenance
 * status to be re-read now; during a live Read-only window a failed write also says why.
 */
function signalMaintenance(error: any): void {
  const status = error?.response?.status;
  if (axios.isCancel(error) || (status !== undefined && status < 500)) return;
  const client = maintenanceClient();
  client.requestCheck();
  const live = client.currentLive();
  const method = String(error?.config?.method ?? "get").toLowerCase();
  if (method !== "get" && live && live.window.impact === "READ_ONLY") {
    const end = live.window.endedAt !== null ? live.window.endedAt : live.window.endsAt;
    window.dispatchEvent(
      new CustomEvent(SAVING_PAUSED_EVENT, { detail: { untilMs: end === null ? null : Date.parse(end) } })
    );
  }
}

export default apiInterceptor;
